import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import InitContext from "../pages/InitContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faSortUp,
  faSortDown,
  faEdit,
  faEye,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import {formatDateForGui, getPatientName, sortData} from "../pages/utils";
import drug from "../../resources/img/pills.svg";
import symptom from "../../resources/img/symptom.svg";
import flask from "../../resources/img/flask.png";
import bloodPressure from "../../resources/img/bloodpressure1.svg";
import PatientDrugs from "./PatientDrugs";
import PatientSymptoms from "./PatientSymptoms";
import PatientLabTests from "./PatientLabTests";
import PatientMeasurements from "./PatientMeasurements";
import Spinner from "../spinner/Spinner";
import ReportPrint from "../appHeader/ReportPrint";
import { faPrint } from "@fortawesome/free-solid-svg-icons/faPrint";
import PatientDataDeleteDialog from "./PatientDataDeleteDialog";
import {Consts} from "../pages/consts";
import PatientPGx from "./PatientPGx";
import {Tooltip} from "@mui/material";
import PatientDisease from "./PatientDisease";

const Patients = ({ parentLoading, parentErrorMessage }) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);

  const [sortConfig, setSortConfig] = useState({
    key: Consts.MED_PLAN,
    ascending: true, // actually means false, since calling sortData in useEffect toggle this
  });

  const [patients, setPatients] = useState(context?.patients || []);
  const [sortedPatients, setSortedPatients] = useState(patients);
  const [showPatientPGxDialog, setShowPatientPGxDialog] = useState(false);
  const [showDiseaseDialog, setShowDiseaseDialog] = useState(false);
  const [showPatientDrugsDialog, setShowPatientDrugsDialog] = useState(false);
  const [showPatientSymptomsDialog, setShowPatientSymptomsDialog] = useState(false);
  const [showPatientLabTestsDialog, setShowPatientLabTestsDialog] = useState(false);
  const [showPatientMeasurementsDialog, setShowPatientMeasurementsDialog] = useState(false);
  const [printReport, setPrintReport] = useState(false);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const columns = [
    Consts.PATIENT,
    Consts.HOSPITAL,
    Consts.DATE_OF_BIRTH,
    Consts.GENDER,
    Consts.DISEASES,
    Consts.PGX,
    Consts.MED_PLAN,
    Consts.SYMPTOMS,
    Consts.LAB_TESTS,
    Consts.MEASUREMENTS,
    "",
  ];

  const spinner = parentLoading && <Spinner />;

  useEffect(() => {
    patients && sortPatients(sortConfig?.key); // Initial sort based on sortConfig
  }, [patients]);

  const isPrescriber = context?.role === Consts.HCP_PRESCRIBER;
  const isStudyDirector = context?.role === Consts.STUDY_DIRECTOR;

  const sortPatients = (key) => {
    const sortFieldMap = {
      [Consts.HOSPITAL]: "affiliation",
      [Consts.DATE_OF_BIRTH]: "year_of_birth",
      [Consts.PGX]: "pgx_test",
      [Consts.MED_PLAN]: "last_prescription",
      [Consts.SYMPTOMS]: "last_symptom",
      [Consts.LAB_TESTS]: "last_lab_test",
      [Consts.MEASUREMENTS]: "last_measurement",
    };

    const sortField = sortFieldMap[key] || key;
    const ascending = sortConfig?.key === key ? !sortConfig?.ascending : true;

    const sortedPats = sortData(
      key,
      patients,
      ascending,
        sortedPat => sortField === Consts.PATIENT
        ? getPatientName(sortedPat, isStudyDirector)
        : sortedPat[sortField]
    );

    setSortedPatients(sortedPats);
    setSortConfig({ key, ascending });
  };

  const handleCloseDiseaseDialog = () => {
    setShowDiseaseDialog(false);
  };

  const handleClosePatientPGxDialog = () => {
    setShowPatientPGxDialog(false);
  };

  const handleClosePatientDrugsDialog = () => {
    setShowPatientDrugsDialog(false);
  };

  const handleClosePatientSymptomsDialog = () => {
    setShowPatientSymptomsDialog(false);
  };

  const handleClosePatientLabTestsDialog = () => {
    setShowPatientLabTestsDialog(false);
  };

  const handleClosePatientMeasurementsDialog = () => {
    setShowPatientMeasurementsDialog(false);
  };

  const handlePrintReportFinish = () => {
    setPrintReport(false);
  };

  const handleConfirmPatientDelete = async () => {
    setPatients((prevPatients) =>
      prevPatients?.filter((patient) => patient?.id !== selectedPatient?.id),
    );

    setSelectedPatient(null);
    setShowConfirmDeleteDialog(false);
  };

  const handleCancelPatientDelete = () => {
    setPatients((prevPatients) =>
      prevPatients?.map((patient) =>
        patient?.id === selectedPatient?.id
          ? { ...patient, delete_requested: 0 }
          : patient,
      ),
    );

    setShowConfirmDeleteDialog(false);
  };

  const handlePrintClick = (sortedPat) => {
    setSelectedPatient(sortedPat);
    setPrintReport(true);
  }

  const updatePatientPgx = (dbGenes) => {
    const maxIdGene = dbGenes?.reduce((prev, current) => current.id > prev.id ? current : prev);

    setPatients((prevPatients) =>
      prevPatients?.map((patient) =>
        patient?.id === selectedPatient?.id && maxIdGene
          ? { ...patient, pgx_test: maxIdGene?.testDate}
          : patient,
      ),
    );
  }

  const updatePatientDisease = (startDate) => {
    setPatients((prevPatients) =>
      prevPatients?.map((patient) =>
        patient?.id === selectedPatient?.id
          ? { ...patient, diseaseDate: startDate}
          : patient,
      ),
    );
  }

  return (
    <div className="w3-display-container content-container">
      <div style={{ width: '100%', textAlign: 'right', paddingRight: "20px" }}>
        <label>{t("username")}: {context?.username}, {t("role")}: {context?.role}</label>
      </div>
      <h2 className="disclaimer-text w3-center disclaimer-background">
        {t("title_disclaimer_hcp")}
      </h2>
      {showDiseaseDialog && (
        <PatientDisease patient={selectedPatient} updatePatient={updatePatientDisease} showDialog={showDiseaseDialog}
                    onCloseDialog={handleCloseDiseaseDialog} />
      )}
      {showPatientPGxDialog && (
        <PatientPGx patient={selectedPatient} updatePatient={updatePatientPgx} showDialog={showPatientPGxDialog}
                    onCloseDialog={handleClosePatientPGxDialog} />
      )}
      {showPatientDrugsDialog && (
        <PatientDrugs
          showDialog={showPatientDrugsDialog}
          onCloseDialog={handleClosePatientDrugsDialog}
          patient={selectedPatient}
          isStudyDirector={isStudyDirector}
        />
      )}
      {showPatientSymptomsDialog && (
        <PatientSymptoms
          showDialog={showPatientSymptomsDialog}
          onCloseDialog={handleClosePatientSymptomsDialog}
          patient={selectedPatient}
          isStudyDirector={isStudyDirector}
        />
      )}
      {showPatientLabTestsDialog && (
        <PatientLabTests
          showDialog={showPatientLabTestsDialog}
          onCloseDialog={handleClosePatientLabTestsDialog}
          patient={selectedPatient}
          isStudyDirector={isStudyDirector}
        />
      )}
      {showPatientMeasurementsDialog && (
        <PatientMeasurements
          showDialog={showPatientMeasurementsDialog}
          onCloseDialog={handleClosePatientMeasurementsDialog}
          patient={selectedPatient}
          isStudyDirector={isStudyDirector}
        />
      )}
      {printReport && (
        <ReportPrint selectedPatient={selectedPatient} onFinishPrintReport={handlePrintReportFinish} />
      )}
      {showConfirmDeleteDialog && (
        <PatientDataDeleteDialog
          showDialog={showConfirmDeleteDialog}
          patient={selectedPatient}
          onCancelDialog={() => setShowConfirmDeleteDialog(false)}
          onConfirmPatientDelete={handleConfirmPatientDelete}
          onCancelPatientDelete={handleCancelPatientDelete}
        />
      )}
      <div className="w3-display-middle form-patients-display">
        <div className="w3-panel w3-card-4 record-panel">
          <header className="w3-container w3-center">
            <h2>{t("my_patients")}</h2>
          </header>
          {parentErrorMessage}
          {spinner}
          <div className="w3-container w3-padding-small form-content-scroll">
            {sortedPatients?.length > 0 ? (
              <div className="w3-responsive">
                <table className="w3-table">
                  <thead className="w3-border-bottom w3-border-blue row-header">
                    <tr>
                      {columns?.filter((column) => isStudyDirector
                        || column !== Consts.HOSPITAL)?.map((header, index) => {
                          if (!header) {
                            return (<th key={`empty-${index}`}><label /></th>);
                          }

                          const background = header === Consts.PATIENT || header === Consts.HOSPITAL
                          || header === Consts.GENDER || header === Consts.MED_PLAN || header === Consts.LAB_TESTS
                              ? "w3-theme-l2"
                              : "w3-theme-l3";

                          return (
                            <th
                              key={`${header}-${index}`}
                              onClick={() => sortPatients(header)}
                              className={`${background} sort-header`}
                            >
                              {header === Consts.MED_PLAN && (
                                <img src={drug} alt={Consts.DRUG} className="icon-block-small" />
                              )}
                              {header === Consts.SYMPTOMS && (
                                <img src={symptom} alt={Consts.SYMPTOM} className="icon-block-small" />
                              )}
                              {header === Consts.LAB_TESTS && (
                                <img src={flask} alt="lab test" className="icon-block-small" />
                              )}
                              {header === Consts.MEASUREMENTS && (
                                <img src={bloodPressure} alt={Consts.MEASUREMENT} className="icon-block-small" />
                              )}
                              <label>{t(header)}</label>
                              {sortConfig?.key === header && (
                                <FontAwesomeIcon
                                  icon={sortConfig?.ascending ? faSortUp : faSortDown}
                                  className="w3-right"
                                />
                              )}
                            </th>
                          );
                        })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPatients?.map((sortedPat, index) => (
                      <tr key={index}>
                        <td className="w3-theme-l2">
                          <label>{getPatientName(sortedPat, isStudyDirector)}</label>
                        </td>
                        {isStudyDirector && (<td className="w3-theme-l2"><label>{sortedPat?.affiliation}</label></td>)}
                        <td className="w3-theme-l3"><label>{sortedPat?.year_of_birth}</label></td>
                        <td className="w3-theme-l2"><label>{t(sortedPat?.gender)}</label></td>
                        <td className="w3-theme-l3">
                          <label>{formatDateForGui(sortedPat?.diseaseDate)}</label>
                          {isPrescriber && <button
                            onClick={() => {
                              setSelectedPatient(sortedPat);
                              setShowDiseaseDialog(true);
                            } }
                            className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                          >
                            <FontAwesomeIcon icon={faEdit}/>
                          </button>}
                          {!isPrescriber && sortedPat?.diseaseDate && <button
                            onClick={() => {
                              setSelectedPatient(sortedPat);
                              setShowDiseaseDialog(true);
                            }}
                            className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                          >
                            <FontAwesomeIcon icon={faEye}/>
                          </button>}
                        </td>
                        <td className="w3-theme-l2">
                          <label>{formatDateForGui(sortedPat?.pgx_test)}</label>
                          {isPrescriber && <button
                            onClick={() => {
                              setSelectedPatient(sortedPat);
                              setShowPatientPGxDialog(true);
                            }}
                            className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>}
                          {!isPrescriber && sortedPat?.pgx_test && <button
                            onClick={() => {
                              setSelectedPatient(sortedPat);
                              setShowPatientPGxDialog(true);
                            }}
                            className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>}
                        </td>
                        <td className="w3-theme-l3">
                          <label>{formatDateForGui(sortedPat?.last_prescription)}</label>
                          {isPrescriber && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowPatientDrugsDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          )}
                          {!isPrescriber && sortedPat?.last_prescription && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowPatientDrugsDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          )}
                        </td>
                        <td className="w3-theme-l2">
                          <label>{formatDateForGui(sortedPat?.last_symptom)}</label>
                          {isPrescriber && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowPatientSymptomsDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          )}
                          {!isPrescriber && sortedPat?.last_symptom && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowPatientSymptomsDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          )}
                        </td>
                        <td className="w3-theme-l3">
                          <label>{formatDateForGui(sortedPat?.last_lab_test)}</label>
                          {isPrescriber && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowPatientLabTestsDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          )}
                          {!isPrescriber && sortedPat?.last_lab_test && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowPatientLabTestsDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          )}
                        </td>
                        <td className="w3-theme-l2">
                          <label>{formatDateForGui(sortedPat?.last_measurement)}</label>
                          {isPrescriber && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowPatientMeasurementsDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          )}
                          {!isPrescriber && sortedPat?.last_measurement && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowPatientMeasurementsDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          )}
                        </td>
                        <td>
                          <Tooltip title={t("export")} placement="top"
                                   componentsProps={{tooltip: {sx: {fontSize: '1.3rem'}}}}>
                            <button onClick={() => handlePrintClick(sortedPat)}
                                    className="w3-btn w3-round-xlarge w3-theme-d5 small-button"
                                    disabled={printReport}>
                              <FontAwesomeIcon icon={faPrint} />
                            </button>
                          </Tooltip>
                          { sortedPat?.delete_requested === 1 && (
                            <button
                              onClick={() => {
                                setSelectedPatient(sortedPat);
                                setShowConfirmDeleteDialog(true);
                              }}
                              className="w3-btn w3-round-xlarge w3-theme-d5 w3-right small-button"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </td>
                      </tr>)
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              !spinner && (<p className="w3-center">{t("you_have_not_yet_any_patients")}</p>)
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Patients;
